const API_BASE = 'https://api.wedmin.biz/wedding-hall';

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function request<T = any>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...options?.headers },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `HTTP ${response.status}`);
  }
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const json = await response.json();
    return json.data ?? json;
  }
  return {} as T;
}

// --- 고객 API ---
export const customerApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/customer?${qs}`);
  },
  create: (data: any) =>
    request('/customer', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number, tab = 'OVERVIEW') =>
    request(`/customer/${id}/detail?tab=${tab}`),
  update: (id: number, data: any) =>
    request(`/customer/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/customer/${id}`, { method: 'DELETE' }),
  memoList: (customerId: number) =>
    request(`/customer/${customerId}/memo`),
  memoCreate: (customerId: number, data: any) =>
    request(`/customer/${customerId}/memo`, { method: 'POST', body: JSON.stringify(data) }),
  memoUpdate: (id: number, data: any) =>
    request(`/customer/memo/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  memoDelete: (id: number) =>
    request(`/customer/memo/${id}`, { method: 'DELETE' }),
  // 계약 품목 (고객=계약 통합)
  addItem: (customerId: number, data: any) =>
    request(`/customer/${customerId}/item`, { method: 'POST', body: JSON.stringify(data) }),
  updateItem: (itemId: number, data: any) =>
    request(`/customer/item/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteItem: (itemId: number) =>
    request(`/customer/item/${itemId}`, { method: 'DELETE' }),
};

// --- 일정 API ---
export const scheduleApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/schedule?${qs}`);
  },
  create: (data: any) =>
    request('/schedule', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/schedule/${id}`),
  update: (id: number, data: any) =>
    request(`/schedule/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/schedule/${id}`, { method: 'DELETE' }),
};

// --- 결제 API ---
export const paymentApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/payment?${qs}`);
  },
  create: (data: any) =>
    request('/payment', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/payment/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/payment/${id}`, { method: 'DELETE' }),
  refundList: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/payment/refund?${qs}`);
  },
  refundCreate: (data: any) =>
    request('/payment/refund', { method: 'POST', body: JSON.stringify(data) }),
};

// --- 정산 API ---
export const settlementApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement?${qs}`);
  },
  create: (data: any) =>
    request('/settlement', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/settlement/${id}`),
  update: (id: number, data: any) =>
    request(`/settlement/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkSettle: (ids: number[], settledDt: string) =>
    request('/settlement/bulk-settle', { method: 'PATCH', body: JSON.stringify({ ids, settledDt }) }),
  bulkCancel: (ids: number[]) =>
    request('/settlement/bulk-cancel', { method: 'PATCH', body: JSON.stringify({ ids }) }),
};

// --- 홀 관리 API ---
export const venueApi = {
  list: (params?: Record<string, any>) => {
    const qs = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/venue?${qs}`);
  },
  create: (data: any) =>
    request('/venue', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/venue/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/venue/${id}`, { method: 'DELETE' }),
  timeSlotList: (venueId: number) =>
    request(`/venue/${venueId}/time-slot`),
  timeSlotCreate: (venueId: number, data: any) =>
    request(`/venue/${venueId}/time-slot`, { method: 'POST', body: JSON.stringify(data) }),
  timeSlotUpdate: (slotId: number, data: any) =>
    request(`/venue/time-slot/${slotId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  timeSlotDelete: (slotId: number) =>
    request(`/venue/time-slot/${slotId}`, { method: 'DELETE' }),
  availability: (venueId: number, date: string, time?: string) => {
    const qs = time ? `?date=${date}&time=${time}` : `?date=${date}`;
    return request(`/venue/${venueId}/availability${qs}`);
  },
};

// --- SMS API ---
export const smsApi = {
  send: (data: { phone: string; message: string }) =>
    request('/sms/send', { method: 'POST', body: JSON.stringify(data) }),
  bulkSend: (data: { phones: string[]; message: string }) =>
    request('/sms/bulk-send', { method: 'POST', body: JSON.stringify(data) }),
};

// --- 스태프 API ---
export const staffApi = {
  list: (params?: Record<string, any>) => {
    const qs = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/staff?${qs}`);
  },
};

// --- 파트너 업체 API (공통) ---
export const partnerVendorApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return fetch(`https://api.wedmin.biz/order/partner-vendors?${qs}`, { headers: getHeaders() })
      .then(res => res.json())
      .then(json => json.data ?? json);
  },
};

// --- 내 업체 정보 API ---
export const myVendorApi = {
  get: () =>
    fetch('https://api.wedmin.biz/vendor/me', { headers: getHeaders() })
      .then(res => res.json())
      .then(json => json.data ?? json),
};

// --- 홀 타임슬롯 그리드 API ---
export const venueGridApi = {
  grid: (date: string) => request(`/schedule/venue-grid?date=${date}`),
};

// --- 당일 진행표/체크리스트 API ---
export const dayManagementApi = {
  getTimeline: (customerId: number) =>
    request(`/day-management/${customerId}/timeline`),
  addTimeline: (customerId: number, data: any) =>
    request(`/day-management/${customerId}/timeline`, { method: 'POST', body: JSON.stringify(data) }),
  updateTimeline: (id: number, data: any) =>
    request(`/day-management/timeline/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTimeline: (id: number) =>
    request(`/day-management/timeline/${id}`, { method: 'DELETE' }),
  applyTimelineTemplate: (customerId: number) =>
    request(`/day-management/${customerId}/timeline/template`, { method: 'POST' }),
  getChecklist: (customerId: number) =>
    request(`/day-management/${customerId}/checklist`),
  addChecklist: (customerId: number, data: any) =>
    request(`/day-management/${customerId}/checklist`, { method: 'POST', body: JSON.stringify(data) }),
  updateChecklist: (id: number, data: any) =>
    request(`/day-management/checklist/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteChecklist: (id: number) =>
    request(`/day-management/checklist/${id}`, { method: 'DELETE' }),
  applyChecklistTemplate: (customerId: number) =>
    request(`/day-management/${customerId}/checklist/template`, { method: 'POST' }),
};
